import { Paper, Typography, CircularProgress, Box, Grid } from '@mui/material'
import { useGetPartnerVoucherChartQuery } from 'store/dashboard'
import NoDataAvailable from './NoDataAvailable'
import { useTimeRange } from './TimeRangeContext'
import HorizontalBarList from './HorizontalBarList'
import ChartTitle from './ChartTitle'

const TOP_PERFORMER_COLOR = '#4CAF50'
const UNDER_PERFORMER_COLOR = '#FF9800'

interface ProcessedData {
  partnerName: string
  redemptionRate: number
  totalVoucher: number
  totalRedeemed: number
}

const PartnerRedemptionRateChart = () => {
  const { startDate, endDate } = useTimeRange()
  const { data, isLoading, isError, refetch, isFetching } = useGetPartnerVoucherChartQuery({
    startDate,
    endDate
  })

  const processed: ProcessedData[] =
    data?.data
      .filter((d) => d.totalVoucher > 0)
      .map((d) => ({
        partnerName: d.partnerName,
        redemptionRate: Math.round((d.totalRedeemed / d.totalVoucher) * 100),
        totalVoucher: d.totalVoucher,
        totalRedeemed: d.totalRedeemed
      })) ?? []

  const topPerformers = processed
    .filter((d) => d.redemptionRate >= 50)
    .sort((a, b) => a.redemptionRate - b.redemptionRate)

  const underPerformers = processed
    .filter((d) => d.redemptionRate < 50)
    .sort((a, b) => b.redemptionRate - a.redemptionRate)

  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: 'none'
      }}
      elevation={0}
    >
      {isLoading || isFetching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <NoDataAvailable variant='error' onRefresh={refetch} />
      ) : !data ? (
        <NoDataAvailable onRefresh={refetch} />
      ) : processed.length === 0 ? (
        <NoDataAvailable onRefresh={refetch} />
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <ChartTitle tooltip='Displays the top partners with the highest voucher claim rates'>
              Top Partner Performers (&gt;50%)
            </ChartTitle>
            {topPerformers.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 200
                }}
              >
                <Typography color='text.secondary'>
                  No partners with redemption rate &gt;= 50%
                </Typography>
              </Box>
            ) : (
              <HorizontalBarList
                data={topPerformers.map((d) => ({
                  label: d.partnerName,
                  percentage: d.redemptionRate
                }))}
                colors={[TOP_PERFORMER_COLOR]}
              />
            )}
          </Grid>
          <Grid item xs={6}>
            <ChartTitle tooltip='Displays lower-performing partners based on voucher claim rate'>
              Underperforming Partners (&lt; 50%)
            </ChartTitle>
            {underPerformers.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 200
                }}
              >
                <Typography color='text.secondary'>
                  No partners with redemption rate &lt; 50%
                </Typography>
              </Box>
            ) : (
              <HorizontalBarList
                data={underPerformers.map((d) => ({
                  label: d.partnerName,
                  percentage: d.redemptionRate
                }))}
                colors={[UNDER_PERFORMER_COLOR]}
              />
            )}
          </Grid>
        </Grid>
      )}
    </Paper>
  )
}

export default PartnerRedemptionRateChart
